/* eslint-disable max-len */
import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { RappiderAIFileUploadComponent } from '../ai-file-upload/ai-file-upload.component';
import { ActionBehavior, ButtonSize, CrudFormConfig, CrudViewColumnType, CrudViewFormItemType } from '@rappider/rappider-components/utils';
import { EntityConfigInterface, EntityItemInterface, RappiderCardWithIconTitleDescriptionListComponent, RappiderEditFormModule, RappiderButtonModule, RappiderIconModule } from '@rappider/rappider-components';
import { AiCrudViewComponent } from '../ai-crud-view/ai-crud-view.component';
import { EntityField } from '../../models/entity-config.models';
import { EntityService } from '../../services/entity.service';
import { DATA_SOURCES } from './datasources.data';
import { closeButtonIcon, DATA_SOURCE_CREATE_BY_FILE_META_CONFIG, DATA_SOURCE_ENTITY_META_CONFIG, addItemButton, resetButton, updateStatusToPartiallyCompleted } from '../../configs/data-source.entity.config';
import { RappiderDatasourceAddWithFileComponent } from '../datasource-add-with-file/datasource-add-with-file.component';
import { ProjectModelEntitiesComponent } from '../project-model-entities/project-model-entities.component';
import { ActivatedRoute, NavigationEnd, Router, ɵEmptyOutletComponent } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { ProjectModelWithRelations } from '@rappider/rappider-sdk';
import { ProjectModelEntityComponent } from '../project-model-entity/project-model-entity.component';
import { JoyrideModule, JoyrideService } from 'ngx-joyride';
import { BrowserStorageManagementService } from 'libs/shared/src/lib/services/browser-storage-management-service/browser-storage-management.service';
import { FullPaths } from '@rappider/shared/definitions';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { DEFAULT_CREATE_BUTTON_ICON } from 'libs/components/src/lib/components/crud-view/config/constants';
import { Store } from '@ngrx/store';
import { ResetDataSource, UpdateDataSource } from 'libs/data-source/src/lib/state/datasource.actions';

@Component({
  selector: 'rappider-datasources',
  standalone: true,
  imports: [
    CommonModule,
    AiCrudViewComponent,
    RappiderEditFormModule,
    ProjectModelEntitiesComponent,
    RappiderButtonModule,
    ProjectModelEntityComponent,
    JoyrideModule,
    NzTabsModule,
    RappiderDatasourceAddWithFileComponent,
    RappiderIconModule
  ],
  templateUrl: './datasources.component.html',
  styleUrls: ['./datasources.component.scss']
})
export class RappiderDatasourcesComponent implements OnInit, OnDestroy {

  @ViewChild('aiCrudViewComponent') aiCrudViewComponent!: AiCrudViewComponent;

  entityConfig: EntityConfigInterface | undefined;
  entityConfigs: { [key: string]: EntityConfigInterface } = {};
  entityData: Record<string, any> = {};
  activeEntityItem: any;
  joyride: { joyrideStep: string; title: string, text: string } = {
    joyrideStep: 'eighth',
    title: 'View Uploaded Files',
    text: 'This section displays a list of all the files you have uploaded. You will see the most recently uploaded file at the top. If the upload is still in progress, some details may not be available yet. When complete, the file information will be updated. Click “Next” to see more detailed insights.'
  };

  selectedDataSourceType: { name: string; description: string } | undefined;
  datasourceCreateOptions = DATA_SOURCES;

  /* When we display a datasource, we use this var to dislay the project model and data for the datasource */
  projectModelsOfDatasource: ProjectModelWithRelations | undefined;
  viewMode: string;
  subscriptions: Subscription[] = [];

  currentDataSourceId: string | null;

  /* custom template */
  @ViewChild('customTemplate') customTemplate!: TemplateRef<any>;
  detailViewTitleBarActionButtons = [addItemButton];
  addFileTemplateVisibility = false;
  closeButtonIcon = closeButtonIcon;
  retryStatus = ['ai-ingestion-token-limit-failed', 'file-physically-missing', 'file-ingestion-failed', 'ai-ingestion-chunk-generation-failed', 'ai-ingestion-vector-generation-failed'];
  listViewTitleBarActionButtons = [
    { "key": "refresh", "behavior": "EMIT", "text": "Refresh Data", "tooltipText": "Refresh Data", "icon": { "name": "fa fa-solid fa-rotate", "type": "FONT_AWESOME" }, "titleBarView": "dropdown" },
    { "key": "create-item", "type": "default", "text": "Connect Your Data Source", "icon": { "name": "fa fa-regular fa-plus" }, "behavior": "ROUTE", "redirectUrl": "/ai/datasources/create" }
  ]

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private entityService: EntityService,
    private browserStorageManagementService: BrowserStorageManagementService,
    private readonly joyrideService: JoyrideService,
    private store: Store<any>
  ) { }

  ngOnInit() {
    this.entityConfig = this.entityService.generateEntityConfig(DATA_SOURCE_ENTITY_META_CONFIG);
    this.entityConfigs['DataSource'] = this.entityConfig;
    this.setInitialDatasourceParams();
    console.log('Active Entity Config:', this.entityConfig);
    this.subscribeToData();
  }

  ngOnDestroy(): void {
    this.subscriptions?.forEach((subscription) => subscription.unsubscribe());
  }

  setInitialDatasourceParams() {
    this.setQueryParameters({ 'entity': 'DataSource', 'viewMode': 'list' });
  }

  subscribeToData() {
    this.subscriptions = [
      this.subscribeToServiceActivatedRouteQueryParams()
    ];
  }

  subscribeToServiceActivatedRouteQueryParams() {
    return this.route.queryParams.subscribe(q => {
      this.viewMode = q.viewMode;
      const hasCompleted = this.activeEntityItem?.projectFiles?.some(p => p.ingestStatus === 'completed');
      const hasNotCompleted = this.activeEntityItem?.projectFiles?.some(p => p.ingestStatus !== 'completed');
      const updateStatusButtonVisible = ((hasCompleted && hasNotCompleted) && !this.activeEntityItem?.hasEverBeenIngested);
      if (q?.viewMode === 'detail' && q?.entityItemId) {
        if (this.activeEntityItem?.ingestionMetadata?.source === 'manual-upload') {
          if (this.retryStatus.includes(this.activeEntityItem?.syncStatus) && updateStatusButtonVisible) {
            this.detailViewTitleBarActionButtons = [updateStatusToPartiallyCompleted, resetButton, addItemButton];
          } else if (this.retryStatus.includes(this.activeEntityItem?.syncStatus) && !updateStatusButtonVisible) {
            this.detailViewTitleBarActionButtons = [resetButton, addItemButton];
          } else if (!this.retryStatus.includes(this.activeEntityItem?.syncStatus) && updateStatusButtonVisible) {
            this.detailViewTitleBarActionButtons = [updateStatusToPartiallyCompleted, addItemButton];
          } else {
            this.detailViewTitleBarActionButtons = [addItemButton];
          }
        } else {
          if (this.retryStatus.includes(this.activeEntityItem?.syncStatus) && updateStatusButtonVisible) {
            this.detailViewTitleBarActionButtons = [updateStatusToPartiallyCompleted, resetButton];
          } else if (this.retryStatus.includes(this.activeEntityItem?.syncStatus) && !updateStatusButtonVisible) {
            this.detailViewTitleBarActionButtons = [resetButton];
          } else if (!this.retryStatus.includes(this.activeEntityItem?.syncStatus) && updateStatusButtonVisible) {
            this.detailViewTitleBarActionButtons = [updateStatusToPartiallyCompleted];
          } else {
            this.detailViewTitleBarActionButtons = [];
          }
        }
        this.currentDataSourceId = q.entityItemId;
      } else {
        this.currentDataSourceId = null;
      }
    });
  }

  onCardCcsRenderCompleted(renderCompleted: any) {
    console.log(renderCompleted);
    if (renderCompleted === true) {
      if (this.browserStorageManagementService.getLocalStorageItem('tour') === 'eighth') {
        this.showSteps();
      }
    }
  }

  onTitleToolbarButtonClicked(button: any) {
    if (button?.joyride?.joyrideStep === 'fifth' && (this.browserStorageManagementService.getLocalStorageItem('tour') === 'fourth') && (this.browserStorageManagementService.getLocalStorageItem('tour') !== 'nineth')) {
      this.browserStorageManagementService.setLocalStorageItem('tour', 'sixth');
    }
    if (button?.key === 'add-item') {
      this.addFileTemplateVisibility = true;
    }
    if (button?.key === 'update-status-to-partially-completed') {
      this.store.dispatch(UpdateDataSource({ payload: { id: this.currentDataSourceId, body: { hasEverBeenIngested: true } } }));
    }
  }

  onTitleToolbarButtonConfirm(button: any) {
    if (button?.key === 'reset-ingestion') {
      this.store.dispatch(ResetDataSource({ payload: { id: this.currentDataSourceId } }));
    }
  }

  onCardSelectorModalVisibility(visibility) {
    this.addFileTemplateVisibility = visibility;
  }

  onEntitySelected(entity: any) {
    // Handle the selected entity, e.g., navigate to a detail view or perform an action
    console.log('Selected Entity:', entity);
  }


  onEntityItemSelected(item: EntityItemInterface) {
    console.log('Entity item selected:', item);
    this.setQueryParameters({ 'entity': 'DataSource', 'show': 'datasource', 'viewMode': 'detail', 'entityItemId': item?.id || undefined });
    this.projectModelsOfDatasource = item?.models;

    this.activeEntityItem = item;
  }
  // TODO: Implement the logic to handle finding the root mongoDB database, BE should a flag for this
  isProjectRootDatasource(item: any): boolean {
    return item?.connector === 'mongodb' && item?.isDataSyncable === false && item?.environmentVariableKeyForConnectionString === 'databaseConnectionString';
  }

  setQueryParameters(paramsToUpdate: { [key: string]: string | null | undefined }) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: paramsToUpdate,
      queryParamsHandling: 'merge',
    });
  }

  onJoyrideTourDone() {
    this.browserStorageManagementService.setLocalStorageItem('tour', 'done');
    this.router.navigate([FullPaths.AIAssistant]);
  }

  showSteps() {
    this.joyrideService.startTour(
      {
        steps: ['firstOptional', 'secondOptional', 'third', 'fourth', 'fifth', 'sixth', 'seventh', 'eighth', 'nineth'],
        themeColor: '#2d2f34',
        startWith: 'eighth',
        waitingTime: 500,
        customTexts: { next: 'Next', prev: 'Prev', done: 'Done' },
        showPrevButton: false
      }
    ).subscribe(
      (step) => {
      },
      (error) => {
      },
      () => {
      }
    );
  }

  onJoyrideDoneButtonClick() {
    this.browserStorageManagementService.setLocalStorageItem('tour', 'stop');
  }

  onToolbarBackButtonClicked(action) {
    this.projectModelsOfDatasource = undefined;
    this.onCardSelectorModalVisibility(false);
  }

  onSuccessfullySubmitted(submitted) {
    if (submitted === true) {
      this.onCardSelectorModalVisibility(false);
      this.aiCrudViewComponent.onToolbarBackButtonClicked('');
    }
  }

}
